import { angleTo, dist } from '../../core/math';
import type { Actor } from '../../sim/actor';
import { registerBossScript } from '../../sim/ai/brains';
import type { MonsterDef } from '../../sim/registry';
import { shapeHits } from '../../sim/shapes';
import type { SkillDef } from '../../sim/skillTypes';
import { registerStatus } from '../../sim/status';
import type { World } from '../../sim/world';
import { monsterStats } from '../balance';
import { circle, cone, counterWindow, hit, invuln, move, proj, rect, ring, self, stage } from '../classes/kit';
import { mLunge, mMelee } from '../monsters/kit';
import {
  KD,
  ONE_SHOT,
  after,
  announce,
  around,
  bossScript,
  bskill,
  burn,
  castAt,
  chill,
  counterCharge,
  despawn,
  enc,
  freeze,
  hazard,
  helpersOf,
  hpScale,
  kb,
  launch,
  lockSkill,
  lockStagger,
  mech,
  nearestPlayer,
  northOf,
  players,
  playerPoint,
  polarSquare,
  randomPlayer,
  registerEncounter,
  resetStagger,
  shock,
  spawnHelper,
  stillCasting,
  stun,
  tele,
  unlockSkill,
  every,
  exposeStagger,
  bossPhase,
  type EncState,
  type EncounterDef,
} from './common';

/**
 * Colossus Hunts: four solo-arena bosses on a 20-minute timer (the fail timer
 * lives in the instance script; `enrageAfter: 1200` only applies Enraged).
 * Difficulty ramps A→D following the research guidance (§5.6):
 *   A  Cinderhide — telegraphed melee + counter + mandatory shell destruction
 *   B  Gloomfang  — HP-threshold enrage + grab-and-rescue
 *   C  Rimebound  — floor hazards + unstaggerable final phase (+ tail cut)
 *   D  Dunestrider — one-shot with a gimmick + adds (+ impale grab)
 * Spawn each colossus at the centre of its arena; `params.arenaRadius` sizes
 * edge mechanics (override with `setEncounterArena`).
 */

const COLOSSUS_ENRAGE = 1200;

// =============================================================== A · Cinderhide Behemoth (ilvl 250)

const CIN_ATK = 1.0;
const CH = hpScale('boss', CIN_ATK);

registerStatus({ id: 'cinder_shell', name: 'Cinder Shell', kind: 'buff', icon: 'cinder_shell', description: 'Molten plating: damage taken -60% until the shell is destroyed with weak-point attacks.', stacking: 'refresh', mods: { dmgReduction: 0.6 }, color: 0xff7a2a });
registerStatus({ id: 'cinder_overheat', name: 'Overheated', kind: 'buff', icon: 'cinder_overheat', description: 'Attack and move speed +15%.', stacking: 'refresh', mods: { atkSpeedPct: 0.15, moveSpeedPct: 0.15 }, color: 0xff4010 });

const CARDINALS = [0, 90, 180, 270];
const DIAGONALS = [45, 135, 225, 315];

const CINDER_SKILLS: SkillDef[] = [
  bskill({ id: 'cinder_stomp', name: 'Foreleg Stomp', cd: 5, anim: 'Slam', dur: 1.3, element: 'fire', actions: [tele(0, circle(4.5, 3), 0.8, 'red', CH(0.18), { reaction: KD })] }),
  bskill({ id: 'cinder_gore', name: 'Horn Gore', cd: 6, anim: 'Attack', dur: 1.2, actions: [tele(0, cone(6.5, 90), 0.75, 'red', CH(0.2), { reaction: kb(3) })] }),
  counterCharge({ id: 'cinder_horn_charge', name: 'Horn Charge', cd: 12, distance: 16, width: 3.4, windup: 1.3, coef: CH(0.3), element: 'fire' }),
  bskill({ id: 'cinder_magma_breath', name: 'Magma Breath', cd: 10, anim: 'Breath', dur: 2.5, element: 'fire', actions: [hazard(0, cone(13, 70), 1.0, 1.2, 0.3, CH(0.06), 'red', { status: [burn()] })] }),
  bskill({ id: 'cinder_vents', name: 'Cardinal Vents', cd: 18, anim: 'Roar', dur: 2.2, element: 'fire', description: 'Vents erupt along the four cardinals; the diagonals are safe.', actions: CARDINALS.map((r) => tele(0, cone(20, 50, r), 1.6, 'red', CH(0.35), { status: [burn()], reaction: launch(0.6, 0.8) })) }),
  bskill({ id: 'cinder_donut', name: 'Donut Flare', cd: 16, anim: 'Slam', dur: 2.0, element: 'fire', description: 'A ring of flame: stand very close or far away.', actions: [tele(0, ring(5, 16), 1.5, 'red', CH(0.35), { reaction: launch(), status: [burn()] })] }),
  bskill({ id: 'cinder_tail_sweep', name: 'Tail Sweep', cd: 9, anim: 'Attack2', dur: 1.4, actions: [tele(0, cone(8, 140, 180), 0.9, 'yellow', CH(0.18), { reaction: kb(5) })] }),
  bskill({ id: 'cinder_leap', name: 'Body Slam', cd: 14, anim: 'Leap', dur: 1.8, actions: [tele(0, circle(6), 1.25, 'red', CH(0.3), { reaction: KD }, { anchor: 'point' }), move(0.55, 16, 0.6, { toPoint: true, ignoreCollision: true })] }),
  bskill({ id: 'cinder_magma_rain', name: 'Magma Rain', cd: 15, anim: 'Roar', dur: 2.0, element: 'fire', actions: ([[7, 30], [11, 100], [8, 170], [13, 235], [10, 300], [15, 0]] as const).map(([d, a]) => hazard(0, polarSquare(d, a, 4), 1.4, 6, 1, CH(0.08), 'red', { status: [burn()] })) }),
  bskill({ id: 'cinder_quake', name: 'Tri-Quake', cd: 16, anim: 'Slam', dur: 3.0, actions: [tele(0, circle(6), 0.8, 'red', CH(0.2), { reaction: KD }), tele(0, ring(6, 10), 1.6, 'red', CH(0.2), { reaction: KD }), tele(0, ring(10, 14), 2.4, 'red', CH(0.25), { reaction: KD })] }),
  // Mechanic-only skills.
  bskill({ id: 'cinder_cataclysm', name: 'Vent Cataclysm', cd: 0, anim: 'Roar', dur: 3.8, element: 'fire', tags: ['oneshot'], description: 'The cardinal vents erupt for lethal damage. Stand on a diagonal.', actions: CARDINALS.map((r) => tele(0, cone(32, 60, r), 3.0, 'red', CH(ONE_SHOT), { reaction: launch(1, 1.2) })) }),
  bskill({ id: 'cinder_cataclysm_x', name: 'Inverted Cataclysm', cd: 0, anim: 'Roar', dur: 3.8, element: 'fire', tags: ['oneshot'], description: 'The diagonal vents erupt for lethal damage. Stand on a cardinal.', actions: DIAGONALS.map((r) => tele(0, cone(32, 60, r), 3.0, 'red', CH(ONE_SHOT), { reaction: launch(1, 1.2) })) }),
  bskill({ id: 'cinder_magma_tide', name: 'Magma Tide', cd: 0, anim: 'Roar', dur: 2.6, element: 'fire', description: 'Lava floods the edge of the arena.', actions: [hazard(0, ring(17, 40), 2.2, 90, 1, CH(0.1), 'red', { status: [burn(3, 0.15)] }, { anchor: 'point' })] }),
];

const CINDERHIDE: MonsterDef = {
  id: 'cinderhide', name: 'Cinderhide Behemoth', title: 'Colossus', creature: 'Colossus', grade: 'boss', model: 'boss_behemoth', scale: 1, radius: 2.6,
  stats: (l) => monsterStats(l, 'boss', { hp: 1.6, atk: CIN_ATK, speed: 0.95 }),
  skills: CINDER_SKILLS, brain: 'boss', aggroRange: 40, leash: 80, xp: 3000, loot: 'colossus_cinderhide',
  weakness: 'water', stagger: 500, hpBars: 40,
  parts: [
    { id: 'shell', name: 'Cinder Shell', durability: 10 },
    { id: 'horn', name: 'Obsidian Horn', durability: 6 },
  ],
  params: { arenaRadius: 22, ilvl: 250, tier: 'A' },
};

const CINDERHIDE_SCRIPT = bossScript({
  patterns: [
    { skill: 'cinder_stomp', weight: 3, range: [0, 4] },
    { skill: 'cinder_gore', weight: 3, range: [0, 5] },
    { skill: 'cinder_horn_charge', weight: 2, range: [3, 14] },
    { skill: 'cinder_magma_breath', weight: 2, range: [0, 10] },
    { skill: 'cinder_vents', weight: 1.5, range: [0, 18] },
    { skill: 'cinder_donut', weight: 1.5, range: [0, 12] },
    { skill: 'cinder_tail_sweep', weight: 2, range: [0, 7] },
    { skill: 'cinder_leap', weight: 1.5, range: [5, 20], target: 'far' },
    { skill: 'cinder_magma_rain', weight: 1.5, range: [0, 25], phases: [2, 3] },
    { skill: 'cinder_quake', weight: 2, range: [0, 8], phases: [3] },
  ],
  phaseAt: [60, 30],
  enrageAfter: COLOSSUS_ENRAGE,
  mechanics: [
    mech('Magma Tide', { pct: 75 }, 'Magma floods the arena edge — stay near the Behemoth!', (_w, b, e) => castAt(b, 'cinder_magma_tide', e.arena.center)),
    mech('Vent Cataclysm', { pct: 50 }, 'Vent Cataclysm! The cardinal vents are about to erupt — stand on a diagonal!', (_w, b) => castAt(b, 'cinder_cataclysm', () => northOf(b))),
    mech('Inverted Cataclysm', { pct: 20 }, 'Inverted Cataclysm! The diagonal vents glow — stand on a cardinal!', (_w, b) => castAt(b, 'cinder_cataclysm_x', () => northOf(b))),
  ],
  onPhase: (w, b, phase) => {
    if (phase === 3) {
      w.grantStatus(b, { id: 'cinder_overheat', duration: 1e9 }, b.id);
      announce(w, 'The Behemoth overheats!', 'warning');
    }
  },
});

const CINDER_ENCOUNTER: EncounterDef = {
  init: (w, b) => w.grantStatus(b, { id: 'cinder_shell', duration: 1e9 }, b.id),
  watch: (w, b, e) => {
    const parts = b.monster!.parts;
    if (!e.data.shellBroken && parts.find((p) => p.id === 'shell')?.broken) {
      e.data.shellBroken = true;
      w.revokeStatus(b, ['cinder_shell'], b.id);
      announce(w, 'The Cinder Shell shatters — the Behemoth is exposed!', 'success');
    }
    if (!e.data.hornBroken && parts.find((p) => p.id === 'horn')?.broken) {
      e.data.hornBroken = true;
      lockSkill(b, 'cinder_gore');
      announce(w, 'Obsidian Horn broken — no more Horn Gore!', 'success');
    }
  },
};

// =============================================================== B · Gloomfang (ilvl 290)

const GLOOM_ATK = 1.1;
const GH = hpScale('boss', GLOOM_ATK);

registerStatus({ id: 'gloom_grabbed', name: 'Seized', kind: 'debuff', icon: 'gloom_grabbed', description: 'Held in Gloomfang\'s jaws. Allies must stagger it to free you.', stacking: 'strongest', cc: { disable: true }, hardCC: true, color: 0x5a2a8a });
registerStatus({ id: 'gloom_frenzy', name: 'Blood Frenzy', kind: 'buff', icon: 'gloom_frenzy', description: 'Enraged: attack +25%, attack and move speed +25%.', stacking: 'refresh', mods: { atkPct: 0.25, atkSpeedPct: 0.25, moveSpeedPct: 0.25 }, color: 0xff2a4a });

const GRAB_RECT = rect(8, 3.2);

const GLOOM_SKILLS: SkillDef[] = [
  bskill({ id: 'gloom_claw', name: 'Rending Claws', cd: 5, anim: 'Attack', dur: 1.9, actions: [hit(0.6, cone(4.8, 110), GH(0.1), { reaction: { kind: 'flinch' } }), hit(1.0, cone(4.8, 110), GH(0.1), { reaction: { kind: 'flinch' } }), hit(1.5, cone(5.5, 140), GH(0.18), { reaction: kb(3) })] }),
  bskill({
    id: 'gloom_triple_dash', name: 'Umbral Triple Dash', cd: 14, anim: 'Charge', dur: 3.4, tags: ['counterable'], element: 'dark',
    description: 'Glows blue, then dashes three times; each dash leaves a striking afterimage.',
    actions: [
      counterWindow(0.2, 0.9),
      tele(0, rect(12, 3.2), 1.1, 'red', 0),
      move(1.1, 10, 0.35),
      hit(1.15, rect(2.6, 3.2), GH(0.16), { reaction: KD }),
      tele(1.45, rect(10, 3.2, -10), 0.5, 'purple', GH(0.14), { reaction: KD }),
      move(1.9, -10, 0.35),
      hit(1.95, rect(2.6, 3.2, -2.6), GH(0.16), { reaction: KD }),
      move(2.6, 10, 0.35),
      hit(2.65, rect(2.6, 3.2), GH(0.16), { reaction: KD }),
    ],
  }),
  bskill({ id: 'gloom_shadow_cross', name: 'Shadow Cross', cd: 16, anim: 'Roar', dur: 2.1, element: 'dark', description: 'Shadow clones appear at the cardinals and cross through Gloomfang; the diagonals are safe.', actions: [tele(0, rect(30, 3.6, -15), 1.5, 'red', GH(0.3), { reaction: KD }), tele(0, rect(30, 3.6, -15, 90), 1.5, 'red', GH(0.3), { reaction: KD })] }),
  bskill({ id: 'gloom_howl', name: 'Howl of Dread', cd: 18, anim: 'Roar', dur: 1.8, element: 'dark', actions: [tele(0, circle(10), 1.2, 'blue', GH(0.05), { status: [{ id: 'fear', duration: 2 }] })] }),
  bskill({ id: 'gloom_pounce', name: 'Pounce', cd: 12, anim: 'Leap', dur: 1.6, actions: [tele(0, circle(4.5), 1.0, 'red', GH(0.25), { reaction: KD }, { anchor: 'point' }), move(0.45, 18, 0.5, { toPoint: true, ignoreCollision: true })] }),
  bskill({ id: 'gloom_spin', name: 'Tail Spin', cd: 9, anim: 'Attack2', dur: 1.4, actions: [tele(0, circle(6), 0.9, 'yellow', GH(0.18), { reaction: kb(4) })] }),
  bskill({
    id: 'gloom_grab', name: 'Devouring Lunge', cd: 22, anim: 'Charge', dur: 6.0, tags: ['grab', 'oneshot'],
    description: 'Seizes a player, mauls them, then devours them. Stagger Gloomfang to free the victim.',
    actions: [
      tele(0, GRAB_RECT, 1.0, 'blue', 0),
      hit(1.0, GRAB_RECT, GH(0.05), { status: [{ id: 'gloom_grabbed', duration: 5.0 }] }),
      hit(2.0, GRAB_RECT, GH(0.06)),
      hit(3.0, GRAB_RECT, GH(0.06)),
      hit(4.0, GRAB_RECT, GH(0.06)),
      hit(5.2, GRAB_RECT, GH(ONE_SHOT), { vfx: 'heavy', reaction: KD }),
    ],
  }),
  bskill({ id: 'gloom_shadow_bolt', name: 'Shadow Bolts', cd: 7, anim: 'Breath', dur: 1.2, element: 'dark', actions: [proj(0.7, { speed: 16, range: 22, radius: 0.6, count: 3, spreadDeg: 30, hit: { shape: circle(0.6), coef: GH(0.12), reaction: { kind: 'flinch' } }, vfx: 'shadow' })] }),
  bskill({ id: 'gloom_backflip', name: 'Backflip Burst', cd: 11, anim: 'Leap', dur: 1.3, element: 'dark', actions: [tele(0, circle(5), 0.8, 'red', GH(0.2), { reaction: KD }), move(0.2, -8, 0.4)] }),
  bskill({ id: 'gloom_frenzy_rend', name: 'Frenzied Rend', cd: 12, anim: 'Attack', dur: 3.0, actions: [0, 1, 2, 3, 4].flatMap((i) => [move(0.45 + i * 0.5, 3, 0.18), hit(0.6 + i * 0.5, cone(4.5, 100), GH(0.1), { reaction: i === 4 ? kb(3) : { kind: 'flinch' } })]) }),
  // Mechanic-only.
  bskill({ id: 'gloom_clone_assault', name: 'Shadow Hunt', cd: 0, anim: 'Roar', dur: 3.8, element: 'dark', description: 'Clones sweep the arena in a rotating cross.', actions: [0, 90, 45, 135].map((r, i) => tele(0, rect(34, 4, -17, r), 1.4 + i * 0.6, 'purple', GH(0.35), { reaction: KD })) }),
];

const GLOOM_SHADE: MonsterDef = {
  id: 'gloom_shade', name: 'Umbral Shade', grade: 'minion', model: 'boss_gloomfang', scale: 0.7, radius: 0.1, tint: 0x241433,
  stats: (l) => monsterStats(l, 'minion'), skills: [], brain: 'none', aggroRange: 0, leash: 99, xp: 0, loot: null,
  params: { helper: true, material: 'shadow' },
};

const GLOOMFANG: MonsterDef = {
  id: 'gloomfang', name: 'Gloomfang', title: 'Colossus', creature: 'Colossus', grade: 'boss', model: 'boss_gloomfang', scale: 1, radius: 2.0,
  stats: (l) => monsterStats(l, 'boss', { hp: 2.0, atk: GLOOM_ATK, speed: 1.15 }),
  skills: GLOOM_SKILLS, brain: 'boss', aggroRange: 40, leash: 80, xp: 3600, loot: 'colossus_gloomfang',
  weakness: 'holy', stagger: 600, hpBars: 50,
  parts: [{ id: 'mane', name: 'Shadow Mane', durability: 8 }],
  params: { arenaRadius: 22, ilvl: 290, tier: 'B' },
};

const GLOOMFANG_SCRIPT = bossScript({
  patterns: [
    { skill: 'gloom_claw', weight: 3, range: [0, 4] },
    { skill: 'gloom_triple_dash', weight: 2, range: [2, 12] },
    { skill: 'gloom_shadow_cross', weight: 1.5, range: [0, 16] },
    { skill: 'gloom_howl', weight: 1, range: [0, 8] },
    { skill: 'gloom_pounce', weight: 1.5, range: [6, 22], target: 'far' },
    { skill: 'gloom_spin', weight: 2, range: [0, 5] },
    { skill: 'gloom_grab', weight: 1.2, range: [0, 6], phases: [2, 3] },
    { skill: 'gloom_shadow_bolt', weight: 1.5, range: [5, 22] },
    { skill: 'gloom_backflip', weight: 1.5, range: [0, 4] },
    { skill: 'gloom_frenzy_rend', weight: 2.5, range: [0, 6], phases: [3] },
  ],
  phaseAt: [60, 30],
  enrageAfter: COLOSSUS_ENRAGE,
  mechanics: [
    mech('Shadow Hunt', { pct: 65 }, 'Shadow Hunt! Gloomfang\'s clones sweep the arena — watch the rotating lanes!', (_w, b) => castAt(b, 'gloom_clone_assault', () => northOf(b))),
    mech('Devouring Hunt', { pct: 45 }, 'Gloomfang hunts for prey — be ready to stagger it!', (w, b) => castAt(b, 'gloom_grab', playerPoint(w, b, randomPlayer(w)?.id ?? null))),
    mech('Blood Frenzy', { pct: 30 }, 'Gloomfang\'s eyes blaze red — it is enraged!', (w, b) => w.grantStatus(b, { id: 'gloom_frenzy', duration: 1e9 }, b.id)),
  ],
});

function spawnShades(w: World, b: Actor, e: EncState, angles: number[], radius: number, life: number): void {
  const aim = e.cur?.aimAngle ?? b.facing;
  for (const a of angles) {
    const pos = around(b.pos, radius, aim + (a * Math.PI) / 180);
    const shade = spawnHelper(w, b, 'gloom_shade', pos, { facing: angleTo(pos, b.pos) });
    after(b, life, () => despawn(w, shade.id));
  }
}

const GLOOM_ENCOUNTER: EncounterDef = {
  clearOnDeath: ['gloom_grabbed'],
  watch: (w, b, e) => {
    if (!e.data.maneBroken && b.monster!.parts.find((p) => p.id === 'mane')?.broken) {
      e.data.maneBroken = true;
      lockSkill(b, 'gloom_howl');
      announce(w, 'Shadow Mane broken — Gloomfang can no longer howl!', 'success');
    }
  },
  onCastStart: {
    gloom_shadow_cross: (w, b, e) => spawnShades(w, b, e, CARDINALS, 13, 2.0),
    gloom_clone_assault: (w, b, e) => spawnShades(w, b, e, [0, 90, 180, 270, 45, 135, 225, 315], 16, 3.6),
    gloom_grab: (w, b, _e, cur) => {
      after(b, 1.05, () => {
        if (!stillCasting(b, cur.castId)) return;
        const caught = players(w).some((p) => shapeHits(GRAB_RECT, { origin: cur.origin, facing: cur.aimAngle }, p.pos, p.radius));
        if (!caught) return;
        exposeStagger(b, 0.45);
        announce(w, 'Gloomfang seizes its prey — stagger it to break the hold!', 'warning');
      });
    },
  },
  onCastEnd: {
    gloom_grab: (w, b, _e, outcome) => {
      for (const p of players(w)) w.revokeStatus(p, ['gloom_grabbed'], b.id);
      if (outcome === 'staggered' || outcome === 'countered') announce(w, 'The victim is torn free!', 'success');
    },
  },
};

// =============================================================== C · Rimebound Wyrm (ilvl 330)

const RIME_ATK = 1.2;
const RH = hpScale('boss', RIME_ATK);

registerStatus({ id: 'rime_encased', name: 'Rime-Encased', kind: 'buff', icon: 'rime_encased', description: 'Sealed in protective ice: cannot act, damage taken -90%.', stacking: 'refresh', mods: { dmgReduction: 0.9 }, cc: { disable: true }, color: 0xbfefff });
registerStatus({ id: 'rime_unstaggerable', name: 'Glacial Resolve', kind: 'buff', icon: 'rime_unstaggerable', description: 'Cannot be staggered.', stacking: 'refresh', mods: { staggerTakenPct: -1 }, color: 0x9fe8ff });
registerStatus({ id: 'rime_fury', name: 'Frostfury', kind: 'buff', icon: 'rime_fury', description: 'Attack +15%.', stacking: 'refresh', mods: { atkPct: 0.15 }, color: 0x6fd6ff });
registerStatus({ id: 'rime_overdrive', name: 'Blizzard Heart', kind: 'buff', icon: 'rime_overdrive', description: 'Attack speed +20%, move speed +20%.', stacking: 'refresh', mods: { atkSpeedPct: 0.2, moveSpeedPct: 0.2 }, color: 0xe0f8ff });

const zeroSigils = (angles: number[], d: number) => angles.map((a) => tele(0, polarSquare(d, a, 4.2), 2.8, 'purple', 0, { status: [{ id: 'rime_encased', duration: 2.4 }] }));

const RIME_SKILLS: SkillDef[] = [
  bskill({ id: 'rime_bite', name: 'Glacial Bite', cd: 5, anim: 'Attack', dur: 1.2, element: 'water', actions: [tele(0, cone(5.5, 80), 0.7, 'red', RH(0.2), { reaction: kb(2) })] }),
  bskill({ id: 'rime_tail_sweep', name: 'Tail Sweep', cd: 9, anim: 'Attack2', dur: 1.4, element: 'water', actions: [tele(0, cone(8, 160, 180), 0.9, 'blue', RH(0.15), { status: [stun(1.5)] })] }),
  bskill({
    id: 'rime_icy_breath', name: 'Icy Breath', cd: 13, anim: 'Breath', dur: 3.0, element: 'water', description: 'Inhales (pulling everyone in), then exhales a freezing blast. Dodge sideways between them.',
    actions: [
      { at: 0, kind: 'area', area: { shape: cone(16, 100), delay: 0.3, duration: 1.3, telegraph: 'yellow', pull: 4, hit: { shape: cone(16, 100), coef: 0 } } },
      tele(0, cone(15, 80), 1.9, 'red', RH(0.4), { reaction: kb(4), status: [chill()] }),
    ],
  }),
  bskill({ id: 'rime_freezing_ground', name: 'Freezing Ground', cd: 12, anim: 'Slam', dur: 1.8, element: 'water', actions: [4, 9, 14].flatMap((off) => [tele(0, circle(3, off), 1.1, 'blue', RH(0.12), { status: [freeze(1.5)] }), hazard(0, circle(3, off), 1.1, 8, 1, RH(0.03), 'blue', { status: [chill(2)] })]) }),
  bskill({ id: 'rime_frost_lanes', name: 'Frost Lanes', cd: 16, anim: 'Roar', dur: 2.0, element: 'water', actions: DIAGONALS.flatMap((r) => [tele(0, rect(22, 3, 0, r), 1.3, 'blue', RH(0.14), { status: [freeze(1.2)] }), hazard(0, rect(22, 3, 0, r), 1.3, 10, 1, RH(0.04), 'blue', { status: [chill(2)] })]) }),
  bskill({ id: 'rime_spin', name: 'Wyrm Spin', cd: 10, anim: 'Attack2', dur: 1.5, actions: [tele(0, circle(6.5), 1.0, 'yellow', RH(0.22), { reaction: launch() })] }),
  bskill({ id: 'rime_tail_slam', name: 'Tail Slams', cd: 12, anim: 'Slam', dur: 2.5, actions: [0.8, 1.4, 2.0].map((d) => tele(0, circle(4.5, -5), d, 'red', RH(0.12), { reaction: launch(0.6, 0.8) })) }),
  bskill({ id: 'rime_ice_orb', name: 'Ice Orb', cd: 8, anim: 'Breath', dur: 1.3, element: 'water', actions: [proj(0.8, { speed: 9, range: 18, radius: 0.9, hit: { shape: circle(0.9), coef: 0 }, explode: { shape: circle(3), coef: RH(0.16), status: [chill()], reaction: { kind: 'flinch' } }, vfx: 'ice' })] }),
  bskill({ id: 'rime_ice_orb_triple', name: 'Ice Orb Volley', cd: 10, anim: 'Breath', dur: 1.3, element: 'water', actions: [proj(0.8, { speed: 9, range: 18, radius: 0.9, count: 3, spreadDeg: 50, hit: { shape: circle(0.9), coef: 0 }, explode: { shape: circle(3), coef: RH(0.16), status: [chill()], reaction: { kind: 'flinch' } }, vfx: 'ice' })] }),
  bskill({
    id: 'rime_dive', name: 'Dive & Breach', cd: 24, element: 'water', description: 'Dives under the ice and chases a player, then breaches beneath them. Outrun it together.',
    stages: [stage('Walk', 3.2, [invuln(0, 3.4)], { moveMult: 1.05 }), stage('Slam', 1.4, [tele(0, circle(5.5), 0.9, 'red', RH(0.5), { reaction: launch(1.2, 1.2) })])],
  }),
  counterCharge({ id: 'rime_counter_charge', name: 'Rime Charge', cd: 13, distance: 14, width: 3.6, windup: 1.2, coef: RH(0.32), element: 'water' }),
  bskill({ id: 'rime_blizzard', name: 'Blizzard Ring', cd: 18, anim: 'Roar', dur: 2.1, element: 'water', actions: [tele(0, ring(7, 20), 1.6, 'blue', RH(0.3), { status: [freeze(1.5)] })] }),
  // Mechanic-only.
  bskill({ id: 'rime_absolute_zero', name: 'Absolute Zero', cd: 0, anim: 'Roar', dur: 4.6, element: 'water', tags: ['oneshot'], description: 'Stand in a rime sigil to be sealed in protective ice before the arena flash-freezes.', actions: [...zeroSigils(DIAGONALS, 9), tele(0, circle(45), 3.6, 'red', RH(ONE_SHOT), { status: [freeze(2)] })] }),
  bskill({ id: 'rime_absolute_zero_2', name: 'Absolute Zero', cd: 0, anim: 'Roar', dur: 4.6, element: 'water', tags: ['oneshot'], description: 'Stand in a rime sigil to be sealed in protective ice before the arena flash-freezes.', actions: [...zeroSigils(CARDINALS, 11), tele(0, circle(45), 3.6, 'red', RH(ONE_SHOT), { status: [freeze(2)] })] }),
  bskill({ id: 'rime_frozen_lake', name: 'Frozen Lake', cd: 0, anim: 'Slam', dur: 2.6, element: 'water', description: 'The lake around the arena cracks and freezes.', actions: [hazard(0, ring(19, 40), 2.0, 240, 1, RH(0.06), 'blue', { status: [chill(2)] }, { anchor: 'point' })] }),
];

const RIMEBOUND: MonsterDef = {
  id: 'rimebound', name: 'Rimebound Wyrm', title: 'Colossus', creature: 'Colossus', grade: 'boss', model: 'boss_wyrm', scale: 1, radius: 2.8,
  stats: (l) => monsterStats(l, 'boss', { hp: 2.4, atk: RIME_ATK, speed: 1.0 }),
  skills: RIME_SKILLS, brain: 'boss', aggroRange: 40, leash: 80, xp: 4200, loot: 'colossus_rimebound',
  weakness: 'fire', stagger: 650, hpBars: 60,
  parts: [{ id: 'tail', name: 'Frozen Tail', durability: 10 }],
  params: { arenaRadius: 24, ilvl: 330, tier: 'C' },
};

const RIMEBOUND_SCRIPT = bossScript({
  patterns: [
    { skill: 'rime_bite', weight: 3, range: [0, 4] },
    { skill: 'rime_tail_sweep', weight: 2, range: [0, 7] },
    { skill: 'rime_icy_breath', weight: 1.5, range: [0, 12] },
    { skill: 'rime_freezing_ground', weight: 2, range: [0, 15] },
    { skill: 'rime_frost_lanes', weight: 1.5, range: [0, 18] },
    { skill: 'rime_spin', weight: 2, range: [0, 5] },
    { skill: 'rime_tail_slam', weight: 1.5, range: [0, 8] },
    { skill: 'rime_ice_orb', weight: 1.5, range: [4, 18], phases: [1, 2] },
    { skill: 'rime_ice_orb_triple', weight: 1.5, range: [4, 18], phases: [3] },
    { skill: 'rime_dive', weight: 1, range: [0, 30], target: 'random' },
    { skill: 'rime_counter_charge', weight: 2, range: [3, 14] },
    { skill: 'rime_blizzard', weight: 1.5, range: [0, 10], phases: [2, 3] },
  ],
  phaseAt: [60, 25],
  enrageAfter: COLOSSUS_ENRAGE,
  mechanics: [
    mech('Frozen Lake', { pct: 80 }, 'The frozen lake cracks — the arena edge is freezing!', (_w, b, e) => castAt(b, 'rime_frozen_lake', e.arena.center)),
    mech('Absolute Zero', { pct: 50 }, 'Absolute Zero! Step into a rime sigil to be sealed in protective ice!', (_w, b) => castAt(b, 'rime_absolute_zero', () => northOf(b))),
    mech('Absolute Zero II', { pct: 20 }, 'Absolute Zero again — the sigils have moved!', (_w, b) => castAt(b, 'rime_absolute_zero_2', () => northOf(b))),
  ],
  onPhase: (w, b, phase) => {
    if (phase === 2) {
      w.grantStatus(b, { id: 'rime_fury', duration: 1e9 }, b.id);
      announce(w, 'Rimebound\'s scales glow with frost fury!', 'warning');
    }
    if (phase === 3) {
      w.grantStatus(b, { id: 'rime_unstaggerable', duration: 1e9 }, b.id);
      w.grantStatus(b, { id: 'rime_overdrive', duration: 1e9 }, b.id);
      lockStagger(b);
      announce(w, 'Blizzard Heart — Rimebound can no longer be staggered!', 'warning');
    }
  },
});

/** Steers a burrowing/diving boss toward its chosen victim during the first stage. */
function steerChase(w: World, b: Actor, e: EncState, skillId: string): void {
  const c = b.cast;
  if (!c || c.skill.id !== skillId || c.stage !== 0) return;
  const victim = w.actors.get((e.data[`${skillId}_victim`] as number | undefined) ?? -1);
  const goal = victim?.alive ? victim.pos : nearestPlayer(w, b.pos)?.pos;
  if (!goal) return;
  b.path = dist(b.pos, goal) > 0.5 ? [{ ...goal }] : [];
  b.moveGoal = b.path.length > 0 ? { ...goal } : null;
}

const RIME_ENCOUNTER: EncounterDef = {
  watch: (w, b, e) => {
    if (!e.data.tailCut && b.monster!.parts.find((p) => p.id === 'tail')?.broken) {
      e.data.tailCut = true;
      lockSkill(b, 'rime_tail_sweep');
      lockSkill(b, 'rime_tail_slam');
      announce(w, 'Tail severed — Rimebound loses its tail attacks!', 'success');
    }
    // Phase 3: stagger stays locked even if a stagger recovery timer was running.
    if (b.hasStatus('rime_unstaggerable') || bossPhase(b) >= 3) lockStagger(b);
    steerChase(w, b, e, 'rime_dive');
  },
  onCastStart: {
    rime_dive: (w, _b, e, cur) => {
      e.data.rime_dive_victim = nearestPlayer(w, cur.aimPoint)?.id ?? -1;
      announce(w, 'Rimebound dives beneath the ice — keep moving!', 'warning');
    },
  },
};

// =============================================================== D · Dunestrider (ilvl 370)

const DUNE_ATK = 1.3;
const DH = hpScale('boss', DUNE_ATK);

registerStatus({ id: 'dune_charged', name: 'Electric Charge', kind: 'buff', icon: 'dune_charged', description: 'Crackling with stored lightning: attack +20%, lightning attacks unlocked.', stacking: 'refresh', mods: { atkPct: 0.2 }, color: 0x8fd0ff });
registerStatus({ id: 'dune_overcharged', name: 'Overcharged', kind: 'buff', icon: 'dune_overcharged', description: 'Attack speed +20%.', stacking: 'refresh', mods: { atkSpeedPct: 0.2 }, color: 0xcfe8ff });
registerStatus({ id: 'dune_impaled', name: 'Impaled', kind: 'debuff', icon: 'dune_impaled', description: 'Skewered on the stinger. Allies must stagger Dunestrider to free you.', stacking: 'strongest', cc: { disable: true }, hardCC: true, color: 0xb08a3a });
registerStatus({ id: 'dune_grounded', name: 'Glass-Grounded', kind: 'buff', icon: 'dune_grounded', description: 'Standing on fused glass: lightning damage taken -90%.', stacking: 'refresh', mods: { dmgReduction: 0.9 }, color: 0xe8d8a0 });

const IMPALE_RECT = rect(9, 2.8);

const DUNE_SKILLS: SkillDef[] = [
  bskill({ id: 'dune_pincer', name: 'Pincer Combo', cd: 5, anim: 'Attack', dur: 1.6, actions: [hit(0.65, cone(4.8, 100), DH(0.14), { reaction: { kind: 'flinch' } }), hit(1.05, cone(5, 110), DH(0.16), { reaction: kb(2.5) })] }),
  counterCharge({ id: 'dune_counter_charge', name: 'Sandstorm Charge', cd: 12, distance: 18, width: 3.4, windup: 1.2, coef: DH(0.3), element: 'earth' }),
  bskill({ id: 'dune_electric_charge', name: 'Electric Charge', cd: 40, anim: 'Roar', dur: 3.2, element: 'lightning', description: 'Stores lightning (a long, safe damage window); afterwards its lightning attacks unlock.', actions: [self(2.9, { id: 'dune_charged', duration: 35 })] }),
  bskill({ id: 'dune_jump', name: 'Crushing Leap', cd: 10, anim: 'Leap', dur: 1.6, actions: [tele(0, circle(5), 1.0, 'red', DH(0.22), { reaction: launch() }, { anchor: 'point' }), move(0.4, 12, 0.5, { toPoint: true, ignoreCollision: true })] }),
  bskill({ id: 'dune_tail_swipe', name: 'Stinger Sweep', cd: 9, anim: 'Attack2', dur: 1.4, actions: [tele(0, cone(9, 180, 180), 0.9, 'red', DH(0.25), { reaction: KD })] }),
  bskill({ id: 'dune_breath', name: 'Decaying Breath', cd: 11, anim: 'Breath', dur: 2.2, element: 'earth', actions: [-25, 0, 25].map((r, i) => tele(0.4 * i, cone(8, 50, r), 0.7, 'red', DH(0.08), { status: [{ id: 'weaken', duration: 6, value: -0.15 }] })) }),
  bskill({
    id: 'dune_burrow', name: 'Burrow & Chase', cd: 26, element: 'earth', description: 'Burrows and chases a player, then erupts beneath them.',
    stages: [stage('Walk', 3.4, [invuln(0, 3.6)], { moveMult: 1.05 }), stage('Slam', 1.4, [tele(0, circle(5.5), 0.9, 'red', DH(0.6), { reaction: launch(1.2, 1.2) })])],
  }),
  bskill({ id: 'dune_summon', name: 'Brood Call', cd: 45, anim: 'Roar', dur: 3.0, description: 'Calls broodlings from the sand (a long, safe damage window). Slain broodlings leave fused glass.', actions: [] }),
  bskill({ id: 'dune_triple_strike', name: 'Triple Lightning', cd: 20, anim: 'Breath', dur: 1.3, element: 'lightning', description: 'Three strikes follow one player; the third is huge. Lead it away from allies.', actions: [tele(0, circle(3), 1.0, 'blue', DH(0.2), { status: [stun(1)] }, { anchor: 'point' })] }),
  bskill({ id: 'dune_strike_2', name: 'Second Strike', cd: 0, anim: 'Breath', dur: 1.2, element: 'lightning', actions: [tele(0, circle(3.5), 0.9, 'blue', DH(0.25), { status: [stun(1)] }, { anchor: 'point' })] }),
  bskill({ id: 'dune_strike_3', name: 'Judgement Strike', cd: 0, anim: 'Slam', dur: 1.6, element: 'lightning', actions: [tele(0, circle(7), 1.3, 'red', DH(0.9), { reaction: launch(1, 1.2), status: [shock()] }, { anchor: 'point' })] }),
  bskill({ id: 'dune_lightning_barrage', name: 'Lightning Barrage', cd: 14, anim: 'Roar', dur: 1.6, element: 'lightning', actions: ([[5, 20], [8, 80], [6, 150], [10, 210], [7, 270], [12, 330], [13, 110], [11, 0]] as const).map(([d, a]) => tele(0, polarSquare(d, a, 3), 1.0, 'red', DH(0.2), { status: [shock()] })) }),
  bskill({
    id: 'dune_impale', name: 'Stinger Impale', cd: 24, anim: 'Attack2', dur: 6.6, element: 'earth', tags: ['grab', 'oneshot'],
    description: 'Two paralysing zaps, then the stinger impales a player and slams them. Stagger Dunestrider to free the victim.',
    actions: [
      tele(0, cone(9, 50), 0.8, 'blue', DH(0.06), { status: [stun(1.0)] }),
      tele(0.9, cone(9, 50), 0.8, 'blue', DH(0.06), { status: [stun(1.0)] }),
      hit(2.0, IMPALE_RECT, DH(0.08), { status: [{ id: 'dune_impaled', duration: 4.4 }] }),
      hit(2.8, IMPALE_RECT, DH(0.07)),
      hit(3.6, IMPALE_RECT, DH(0.07)),
      hit(4.4, IMPALE_RECT, DH(0.07)),
      hit(6.2, IMPALE_RECT, DH(ONE_SHOT), { vfx: 'heavy', reaction: KD }),
    ],
  }),
  bskill({ id: 'dune_sand_spiral', name: 'Sand Spiral', cd: 15, anim: 'Slam', dur: 2.5, element: 'earth', actions: [tele(0, circle(6), 1.0, 'red', DH(0.3), { reaction: KD }), tele(0, ring(6, 18), 2.0, 'red', DH(0.3), { reaction: KD })] }),
  bskill({ id: 'dune_tempest_verdict', name: 'Tempest Verdict', cd: 0, castTime: 8, anim: 'Slam', dur: 1.2, element: 'lightning', tags: ['oneshot'], description: 'Lethal lightning over the whole arena. Only fused glass left by slain broodlings grounds it.', actions: [tele(0, circle(50), 0.5, 'purple', DH(ONE_SHOT), { reaction: KD, status: [shock()] })] }),
];

const DUNE_BROODLING: MonsterDef = {
  id: 'dune_broodling', name: 'Sandsting Broodling', grade: 'normal', model: 'mob_scorpion', scale: 0.9, radius: 0.6, tint: 0xc9a25a, creature: 'Beast',
  stats: (l) => monsterStats(l, 'normal', { hp: 4, speed: 1.1 }),
  skills: [
    mMelee('brood_sting', 'Sting', { shape: cone(2.2, 70), coef: 1.2, windup: 0.5, cd: 2 }),
    mLunge('brood_scuttle', 'Scuttle', { distance: 4, coef: 1.4, windup: 0.6, cd: 7, range: [2.5, 6] }),
  ],
  brain: 'swarm', aggroRange: 60, leash: 80, xp: 20, loot: null,
};

const GLASS_HUSK: MonsterDef = {
  id: 'glass_husk', name: 'Fused Glass', grade: 'minion', model: 'fx_glass_husk', scale: 1, radius: 0.1, tint: 0xf2e6b8,
  stats: (l) => monsterStats(l, 'minion'), skills: [], brain: 'none', aggroRange: 0, leash: 99, xp: 0, loot: null,
  params: { helper: true, protectRadius: 2.6 },
};

const DUNESTRIDER: MonsterDef = {
  id: 'dunestrider', name: 'Dunestrider', title: 'Colossus', creature: 'Colossus', grade: 'boss', model: 'boss_scorpion', scale: 1, radius: 2.6,
  stats: (l) => monsterStats(l, 'boss', { hp: 2.8, atk: DUNE_ATK, speed: 1.1 }),
  skills: DUNE_SKILLS, brain: 'boss', aggroRange: 40, leash: 80, xp: 5000, loot: 'colossus_dunestrider',
  weakness: 'earth', stagger: 700, hpBars: 80,
  parts: [{ id: 'stinger', name: 'Stormglass Stinger', durability: 10 }],
  params: { arenaRadius: 24, ilvl: 370, tier: 'D' },
};

const LIGHTNING_PATTERNS = ['dune_triple_strike', 'dune_lightning_barrage'];

const DUNESTRIDER_SCRIPT = bossScript({
  patterns: [
    { skill: 'dune_pincer', weight: 3, range: [0, 4] },
    { skill: 'dune_counter_charge', weight: 2, range: [3, 15] },
    { skill: 'dune_electric_charge', weight: 1.2, range: [0, 30] },
    { skill: 'dune_jump', weight: 1.5, range: [4, 14] },
    { skill: 'dune_tail_swipe', weight: 2, range: [0, 8] },
    { skill: 'dune_breath', weight: 1.5, range: [0, 8] },
    { skill: 'dune_burrow', weight: 1, range: [0, 30], target: 'random' },
    { skill: 'dune_summon', weight: 0.8, range: [0, 30] },
    { skill: 'dune_triple_strike', weight: 2, range: [0, 30], target: 'random' },
    { skill: 'dune_lightning_barrage', weight: 2, range: [0, 14] },
    { skill: 'dune_impale', weight: 1.2, range: [0, 7] },
    { skill: 'dune_sand_spiral', weight: 2, range: [0, 12], phases: [3] },
  ],
  phaseAt: [65, 30],
  enrageAfter: COLOSSUS_ENRAGE,
  mechanics: [
    mech('Brood Swarm', { pct: 70 }, 'Dunestrider calls its brood from the sand!', (_w, b) => castAt(b, 'dune_summon', () => northOf(b))),
    mech('Tempest Verdict', { pct: 50 }, 'Tempest Verdict! Slay broodlings and stand on their fused glass to ground the lightning!', (_w, b) => {
      castAt(b, 'dune_summon', () => northOf(b));
      castAt(b, 'dune_tempest_verdict', () => northOf(b));
    }),
    mech('Brood Swarm II', { pct: 40 }, 'More broodlings burst from the dunes!', (_w, b) => castAt(b, 'dune_summon', () => northOf(b))),
    mech('Tempest Verdict II', { pct: 20 }, 'Tempest Verdict! Find fused glass before the storm breaks!', (_w, b) => {
      castAt(b, 'dune_summon', () => northOf(b));
      castAt(b, 'dune_tempest_verdict', () => northOf(b));
    }),
  ],
  onPhase: (w, b, phase) => {
    if (phase === 3) {
      w.grantStatus(b, { id: 'dune_overcharged', duration: 1e9 }, b.id);
      announce(w, 'Dunestrider is overcharged!', 'warning');
    }
  },
});

function spawnBrood(w: World, b: Actor): void {
  const alive = helpersOf(w, enc(b), 'dune_broodling').length;
  const n = Math.min(10 - alive, 2 + Math.ceil(w.partySize / 2));
  for (let i = 0; i < n; i++) {
    const pos = around(b.pos, 7 + (i % 2) * 3, (i / Math.max(1, n)) * Math.PI * 2 + w.rng.range(0, 0.6));
    spawnHelper(w, b, 'dune_broodling', pos, { add: true });
  }
}

const DUNE_ENCOUNTER: EncounterDef = {
  clearOnDeath: ['dune_impaled'],
  init: (_w, b) => {
    for (const id of LIGHTNING_PATTERNS) lockSkill(b, id);
  },
  watch: (w, b, e) => {
    // Electric Charge unlocks the lightning moveset while it lasts.
    const charged = b.hasStatus('dune_charged');
    if (charged !== !!e.data.charged) {
      e.data.charged = charged;
      for (const id of LIGHTNING_PATTERNS) {
        if (charged) unlockSkill(b, id);
        else lockSkill(b, id);
      }
    }
    if (!e.data.stingerBroken && b.monster!.parts.find((p) => p.id === 'stinger')?.broken) {
      e.data.stingerBroken = true;
      lockSkill(b, 'dune_tail_swipe');
      lockSkill(b, 'dune_impale');
      announce(w, 'Stormglass Stinger shattered — no more impales!', 'success');
    }
    // Slain broodlings leave fused glass that grounds the Tempest Verdict.
    const done = (e.data.husked as number[] | undefined) ?? [];
    for (const id of e.adds) {
      const add = w.actors.get(id);
      if (!add || add.alive || done.includes(id)) continue;
      done.push(id);
      const husk = spawnHelper(w, b, 'glass_husk', { ...add.pos });
      after(b, 35, () => despawn(w, husk.id));
    }
    e.data.husked = done;
    if (every(e, 'ground', 0.2)) {
      const husks = helpersOf(w, e, 'glass_husk');
      for (const p of players(w)) {
        if (husks.some((h) => dist(h.pos, p.pos) <= 2.6)) w.grantStatus(p, { id: 'dune_grounded', duration: 0.6 }, b.id);
      }
    }
    steerChase(w, b, e, 'dune_burrow');
  },
  onCastStart: {
    dune_summon: (w, b) => after(b, 1.0, () => spawnBrood(w, b)),
    dune_burrow: (w, _b, e, cur) => {
      e.data.dune_burrow_victim = nearestPlayer(w, cur.aimPoint)?.id ?? -1;
    },
    dune_triple_strike: (w, _b, e, cur) => {
      e.data.strikeVictim = nearestPlayer(w, cur.aimPoint)?.id ?? -1;
    },
    dune_tempest_verdict: (_w, b) => lockStagger(b),
    dune_impale: (w, b, _e, cur) => {
      after(b, 2.05, () => {
        if (!stillCasting(b, cur.castId)) return;
        if (!players(w).some((p) => shapeHits(IMPALE_RECT, { origin: cur.origin, facing: cur.aimAngle }, p.pos, p.radius))) return;
        exposeStagger(b, 0.45);
        announce(w, 'A player is impaled — stagger Dunestrider to free them!', 'warning');
      });
    },
  },
  onCastEnd: {
    dune_triple_strike: (w, b, e, outcome) => {
      if (outcome === 'completed') castAt(b, 'dune_strike_2', playerPoint(w, b, (e.data.strikeVictim as number | undefined) ?? null));
    },
    dune_strike_2: (w, b, e, outcome) => {
      if (outcome === 'completed') castAt(b, 'dune_strike_3', playerPoint(w, b, (e.data.strikeVictim as number | undefined) ?? null));
    },
    dune_tempest_verdict: (_w, b) => resetStagger(b, 1),
    dune_impale: (w, b, _e, outcome) => {
      for (const p of players(w)) w.revokeStatus(p, ['dune_impaled'], b.id);
      if (outcome === 'staggered' || outcome === 'countered') announce(w, 'The victim is pulled free!', 'success');
    },
  },
};

// =============================================================== exports

export const COLOSSI: MonsterDef[] = [CINDERHIDE, GLOOMFANG, RIMEBOUND, DUNESTRIDER, GLOOM_SHADE, DUNE_BROODLING, GLASS_HUSK];

export function registerColossusScripts(): void {
  registerEncounter('cinderhide', CINDER_ENCOUNTER);
  registerEncounter('gloomfang', GLOOM_ENCOUNTER);
  registerEncounter('rimebound', RIME_ENCOUNTER);
  registerEncounter('dunestrider', DUNE_ENCOUNTER);
  registerBossScript('cinderhide', CINDERHIDE_SCRIPT);
  registerBossScript('gloomfang', GLOOMFANG_SCRIPT);
  registerBossScript('rimebound', RIMEBOUND_SCRIPT);
  registerBossScript('dunestrider', DUNESTRIDER_SCRIPT);
}
